import { COORDINATE_STATUS } from 'src/routes/trip-tracer/data/trip-tracer.constants';

/** Playback transport state. */
export const PLAYBACK_STATUS = Object.freeze({
    STOPPED: 'stopped',
    PLAYING: 'playing',
    PAUSED: 'paused',
});

/** Frames advanced per second at 1×; the prototype's speed presets. */
export const PLAYBACK_SPEEDS = Object.freeze([1, 2, 5, 10, 20, 50]);
export const DEFAULT_PLAYBACK_SPEED = 20;

/** Milliseconds between frames at a speed (prototype: 1000 / speed, at least 10 ms). */
export const getPlaybackFrameIntervalMs = (speed) => Math.max(10, 1000 / Math.max(1, speed));

/**
 * Sorted, unique observation times across the given points: one playback
 * frame per moment any visible vehicle reported.
 *
 * @param {Array<{ observedAtMs: number|null }>} points
 * @returns {number[]} Epoch milliseconds, ascending
 */
export const buildTimelineFrames = (points) => [
    ...new Set(points.map((point) => point.observedAtMs).filter(Number.isFinite)),
].sort((left, right) => left - right);

/**
 * Index of the last item whose time is at or before `targetMs` (binary search
 * over ascending times), or -1 when every item is later.
 *
 * @param {number} length
 * @param {(index: number) => number} timeAt
 * @param {number} targetMs
 * @returns {number}
 */
const lastIndexAtOrBefore = (length, timeAt, targetMs) => {
    let low = 0;
    let high = length - 1;
    let found = -1;

    while (low <= high) {
        // eslint-disable-next-line no-bitwise
        const mid = (low + high) >> 1;
        if (timeAt(mid) <= targetMs) {
            found = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return found;
};

/**
 * Frame for a timestamp: an exact match, else the closest frame before it,
 * else the first frame (the prototype's rule for seeking from a table row).
 *
 * @param {number[]} frames
 * @param {number|null} targetMs
 * @returns {number} -1 when there are no frames or no time
 */
export const findFrameIndex = (frames, targetMs) => {
    if (!frames.length || !Number.isFinite(targetMs)) {
        return -1;
    }

    return Math.max(0, lastIndexAtOrBefore(frames.length, (index) => frames[index], targetMs));
};

/**
 * Groups chronological points by vehicle, keeping order.
 *
 * @param {Array<{ vehicleId: string }>} points
 * @returns {Map<string, Array>}
 */
export const groupPointsByVehicle = (points) => points.reduce((byVehicle, point) => {
    if (!byVehicle.has(point.vehicleId)) {
        byVehicle.set(point.vehicleId, []);
    }
    byVehicle.get(point.vehicleId).push(point);
    return byVehicle;
}, new Map());

/**
 * A vehicle's latest point at or before the playhead.
 *
 * @param {Array<{ observedAtMs: number|null }>} vehiclePoints - Chronological
 * @param {number|null} playheadMs
 * @returns {Object|null}
 */
const findIndexAtOrBefore = (vehiclePoints, playheadMs) => {
    if (!vehiclePoints?.length || !Number.isFinite(playheadMs)) {
        return -1;
    }

    return lastIndexAtOrBefore(
        vehiclePoints.length,
        (position) => vehiclePoints[position].observedAtMs ?? -Infinity,
        playheadMs,
    );
};

export const findPointAtOrBefore = (vehiclePoints, playheadMs) => {
    const index = findIndexAtOrBefore(vehiclePoints, playheadMs);
    return index >= 0 ? vehiclePoints[index] : null;
};

/**
 * Where each vehicle is at the playhead. `point` is the vehicle's latest
 * report (the active record); `position` is its latest report with valid
 * coordinates (where the marker is drawn), or null when it has none yet.
 *
 * @param {Map<string, Array>} tracks - From groupPointsByVehicle
 * @param {number|null} playheadMs
 * @returns {Array<{ vehicleId: string, point: Object, position: Object|null }>}
 */
export const getPlayheadVehicles = (tracks, playheadMs) => {
    const result = [];
    tracks.forEach((vehiclePoints, vehicleId) => {
        const pointIndex = findIndexAtOrBefore(vehiclePoints, playheadMs);
        if (pointIndex < 0) {
            return;
        }

        const point = vehiclePoints[pointIndex];
        let position = null;
        for (let index = pointIndex; index >= 0; index -= 1) {
            if (vehiclePoints[index].coordinateStatus === COORDINATE_STATUS.VALID) {
                position = vehiclePoints[index];
                break;
            }
        }

        result.push({ vehicleId, point, position });
    });
    return result;
};
